import { useEffect, useReducer, useRef } from 'react';
import { useDIContainer } from '@/container/RouteContainerProvider';
import type { BaseViewModel } from './BaseViewModel';

/**
 * Hook for using a ViewModel in a React component.
 *
 * - Instantiates ViewModel via the nearest DI container (constructor injection).
 *   When wrapped in `<RouteContainerProvider>`, resolves from the route-scoped
 *   child container; otherwise falls back to the root container.
 * - Subscribes to ViewModel changes and triggers re-renders.
 * - Cleans up on unmount (unsubscribe + dispose).
 *
 * @example
 * function UsersPage() {
 *   const vm = useViewModel(UsersPageViewModel);
 *   return <div>{vm.filteredUsers.length} users</div>;
 * }
 */
export function useViewModel<T extends BaseViewModel>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- tsyringe requires any[] for the generic constructor constraint
    ViewModelClass: new (...args: any[]) => T, // arch-check-ignore — tsyringe requires `any[]` for generic constructor constraint
): T {
    const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
    const container = useDIContainer();

    const vmRef = useRef<T | null>(null);
    if (vmRef.current === null) {
        vmRef.current = container.resolve(ViewModelClass);
    }

    const vm = vmRef.current;

    useEffect(() => {
        const unsubscribe = vm.subscribe(() => {
            forceUpdate();
        });

        return () => {
            unsubscribe();
            vm.dispose();
        };
    }, [vm]);

    return vm;
}
